import React, { memo, useState, useCallback, useMemo, useRef } from 'react';
import type { DataGridColumn } from '../DataGrid.types';
import { Checkbox } from './TableRow';

interface VirtualTableBodyProps<T extends Record<string, unknown>> {
  rows: T[];
  columns: DataGridColumn<T>[];
  onRowClick?: (row: T) => void;
  rowClassName?: string | ((row: T, index: number) => string);
  rowHeight?: number;
  containerHeight?: number;
  overscan?: number;
  enableSelection?: boolean;
  isSelected?: (row: T, index?: number) => boolean;
  onToggleSelect?: (row: T, index?: number) => void;
  getRowId?: (row: T, index: number) => string;
}

// Helper function to get stable row ID
function getStableRowId<T extends Record<string, unknown>>(
  row: T,
  index: number,
  getRowId?: (row: T, index: number) => string
): string {
  if (getRowId) {
    return getRowId(row, index);
  }
  // Try common ID fields
  if ('id' in row && row.id !== null && row.id !== undefined) {
    return String(row.id);
  }
  if ('_id' in row && row._id !== null && row._id !== undefined) {
    return String(row._id);
  }
  if ('key' in row && row.key !== null && row.key !== undefined) {
    return String(row.key);
  }
  // Fallback to index-based ID
  return `__vrow_${index}`;
}

function VirtualTableBodyComponent<T extends Record<string, unknown>>({
  rows,
  columns,
  onRowClick,
  rowClassName,
  rowHeight = 48,
  containerHeight = 400,
  overscan = 5,
  enableSelection = false,
  isSelected,
  onToggleSelect,
  getRowId
}: VirtualTableBodyProps<T>): React.ReactElement {
  const [scrollTop, setScrollTop] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const totalHeight = rows.length * rowHeight;

  const { startIndex, endIndex } = useMemo(() => {
    const visibleCount = Math.ceil(containerHeight / rowHeight);
    const startIndex = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
    const endIndex = Math.min(
      rows.length - 1,
      Math.floor(scrollTop / rowHeight) + visibleCount + overscan
    );
    return { startIndex, endIndex };
  }, [scrollTop, containerHeight, rowHeight, rows.length, overscan]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);

  const getRowClassName = (row: T, index: number): string => {
    if (typeof rowClassName === 'function') {
      return rowClassName(row, index);
    }
    return rowClassName || '';
  };

  const getCellValue = (row: T, column: DataGridColumn<T>): React.ReactNode => {
    // Note: cell renderer with full context is not supported in virtual mode
    // Use accessorKey or accessorFn instead
    if (column.accessorFn) {
      const value = column.accessorFn(row);
      if (typeof value === 'number') {
        return value.toLocaleString();
      }
      return String(value ?? '');
    }
    if (column.accessorKey) {
      const value = row[column.accessorKey];
      if (typeof value === 'number') {
        return value.toLocaleString();
      }
      return String(value ?? '');
    }
    return '';
  };

  const visibleRows = useMemo(() => {
    const items: { index: number; row: T }[] = [];
    for (let i = startIndex; i <= endIndex && i < rows.length; i++) {
      items.push({ index: i, row: rows[i] });
    }
    return items;
  }, [rows, startIndex, endIndex]);

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      style={{
        height: containerHeight,
        overflow: 'auto',
        position: 'relative'
      }}
    >
      {/* Spacer for total scroll height */}
      <div style={{ height: totalHeight, position: 'relative' }}>
        {/* Render only visible rows */}
        {visibleRows.map(({ index, row }) => {
          const rowId = getStableRowId(row, index, getRowId);
          const selected = enableSelection && isSelected?.(row, index);
          const isHovered = hoveredIndex === index;

          return (
            <div
              key={rowId}
              className={getRowClassName(row, index)}
              onClick={() => onRowClick?.(row)}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              style={{
                position: 'absolute',
                top: index * rowHeight,
                left: 0,
                right: 0,
                height: rowHeight,
                display: 'flex',
                alignItems: 'center',
                background: selected
                  ? 'var(--color-primary-50)'
                  : isHovered
                    ? 'var(--color-neutral-50)'
                    : index % 2 === 0
                      ? 'var(--color-bg-elevated)'
                      : 'var(--color-bg-secondary)',
                borderBottom: '1px solid var(--color-border-subtle)',
                cursor: onRowClick ? 'pointer' : 'default',
                transition: 'background var(--transition-fast)'
              }}
            >
              {/* Selection checkbox */}
              {enableSelection && (
                <div
                  style={{
                    width: 48,
                    minWidth: 48,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRight: '1px solid var(--color-border-subtle)'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleSelect?.(row, index);
                  }}
                >
                  <Checkbox
                    checked={selected || false}
                    onChange={() => onToggleSelect?.(row, index)}
                  />
                </div>
              )}

              {/* Data cells */}
              {columns.map((column) => (
                <div
                  key={column.id}
                  style={{
                    width: column.width || 150,
                    minWidth: column.minWidth || 50,
                    maxWidth: column.maxWidth,
                    padding: 'var(--space-3) var(--space-4)',
                    fontSize: 'var(--text-sm)',
                    color: 'var(--color-text-primary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    borderRight: '1px solid var(--color-border-subtle)',
                    fontVariantNumeric: 'tabular-nums'
                  }}
                >
                  {getCellValue(row, column)}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export const VirtualTableBody = memo(VirtualTableBodyComponent) as typeof VirtualTableBodyComponent;
